"use client";

import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
type FrameDot = [number, number];
type Frame = FrameDot[];
type Frames = Frame[];
interface MotionGridProps {
  gridSize: [number, number];
  frames: Frames;
  duration?: number;
  animate?: boolean;
  className?: string;
}
interface MotionGridCellsProps {
  className?: string;
  activeClassName?: string;
  inactiveClassName?: string;
}
const MotionGridContext = React.createContext<{
  index: number;
  cols: number;
  rows: number;
  frames: Frames;
  duration: number;
  animate: boolean;
} | null>(null);
function useMotionGrid() {
  const ctx = React.useContext(MotionGridContext);
  if (!ctx) {
    throw new Error("MotionGridCells must be used within MotionGrid");
  }
  return ctx;
}
function MotionGrid(t0) {
  const $ = _c(26);
  const {
    gridSize,
    frames,
    duration: t1,
    animate: t2,
    className,
    children
  } = t0;
  const duration = t1 === undefined ? 200 : t1;
  const animate = t2 === undefined ? true : t2;
  const [index, setIndex] = React.useState(0);
  let t3;
  let t4;
  if ($[0] !== animate || $[1] !== duration || $[2] !== frames.length) {
    t3 = () => {
      if (!animate || frames.length === 0) {
        return;
      }
      const interval = setInterval(() => setIndex(i => (i + 1) % frames.length), duration);
      return () => clearInterval(interval);
    };
    t4 = [frames.length, duration, animate];
    $[0] = animate;
    $[1] = duration;
    $[2] = frames.length;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  React.useEffect(t3, t4);
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setIndex(0);
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== frames) {
    t6 = [frames];
    $[6] = frames;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  React.useEffect(t5, t6);
  const [cols, rows] = gridSize;
  let t7;
  if ($[8] !== animate || $[9] !== cols || $[10] !== duration || $[11] !== frames || $[12] !== index || $[13] !== rows) {
    t7 = {
      animate,
      index,
      cols,
      rows,
      frames,
      duration
    };
    $[8] = animate;
    $[9] = cols;
    $[10] = duration;
    $[11] = frames;
    $[12] = index;
    $[13] = rows;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== className) {
    t8 = cn("grid", className);
    $[15] = className;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const t9 = `repeat(${cols}, minmax(0, 1fr))`;
  let t10;
  if ($[17] !== t9) {
    t10 = {
      gridTemplateColumns: t9,
      gridAutoRows: "1fr"
    };
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== children || $[20] !== t10 || $[21] !== t8) {
    t11 = <div className={t8} style={t10}>{children}</div>;
    $[19] = children;
    $[20] = t10;
    $[21] = t8;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t11 || $[24] !== t7) {
    t12 = <MotionGridContext.Provider value={t7}>{t11}</MotionGridContext.Provider>;
    $[23] = t11;
    $[24] = t7;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  return t12;
}
function MotionGridCells(t0) {
  const $ = _c(16);
  const {
    className,
    activeClassName,
    inactiveClassName
  } = t0;
  const {
    index,
    cols,
    rows,
    frames
  } = useMotionGrid();
  let set;
  if ($[0] !== cols || $[1] !== frames || $[2] !== index) {
    set = new Set();
    const frame = frames[index];
    if (frame) {
      for (const [x, y] of frame) {
        set.add(y * cols + x);
      }
    }
    $[0] = cols;
    $[1] = frames;
    $[2] = index;
    $[3] = set;
  } else {
    set = $[3];
  }
  const active = set;
  let t1;
  if ($[4] !== active || $[5] !== activeClassName || $[6] !== className || $[7] !== cols || $[8] !== inactiveClassName || $[9] !== rows) {
    let t2;
    if ($[11] !== active || $[12] !== activeClassName || $[13] !== className || $[14] !== inactiveClassName) {
      t2 = (_, i) => {
        const isActive = active.has(i);
        return <motion.div key={i} className={cn(className, isActive ? activeClassName : inactiveClassName)} animate={{
          opacity: isActive ? 1 : 0.3
        }} transition={{
          duration: 0.15,
          ease: "easeInOut"
        }} />;
      };
      $[11] = active;
      $[12] = activeClassName;
      $[13] = className;
      $[14] = inactiveClassName;
      $[15] = t2;
    } else {
      t2 = $[15];
    }
    t1 = <>{Array.from({
        length: cols * rows
      }, t2)}</>;
    $[4] = active;
    $[5] = activeClassName;
    $[6] = className;
    $[7] = cols;
    $[8] = inactiveClassName;
    $[9] = rows;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  return t1;
}
export { MotionGrid, MotionGridCells };
export type { Frames, Frame, FrameDot, MotionGridProps, MotionGridCellsProps };